import { useMemo, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { RiArrowDownSLine, RiBuilding2Line, RiCheckLine, RiSearchLine } from '@remixicon/react';
import {
  ALL_MALLS,
  useMallScopeStore,
  type MallSummary,
  type MallScopeValue,
} from '@/stores/mallScopeStore';
import { formatArea, trCollator } from '@/lib/format';

/**
 * AVM SEÇİCİ.
 *
 * Çoklu AVM sisteminde kullanıcının "hangi AVM'ye bakıyorum?" sorusuna her an
 * cevap verebilmesi gerekir. Bu yüzden seçici header'ın SOL tarafında, göz
 * hizasında ve seçili AVM adı açıkça yazılı şekilde duruyor — bir ayar
 * menüsünün içine gizlenmiyor.
 *
 * react-bootstrap Dropdown kullanılıyor çünkü klavye gezinmesi ve odak
 * yönetimi hazır geliyor; kendi yazdığımız bir dropdown'da bunlar genelde
 * eksik kalır.
 */
export function MallSwitcher() {
  const mallId = useMallScopeStore((s) => s.mallId);
  const malls = useMallScopeStore((s) => s.availableMalls);
  const canViewAll = useMallScopeStore((s) => s.canViewAll);
  const recentMallIds = useMallScopeStore((s) => s.recentMallIds);
  const setMall = useMallScopeStore((s) => s.setMall);

  const [query, setQuery] = useState('');

  const activeMall = mallId === ALL_MALLS ? null : malls.find((m) => m.id === mallId);

  const { recent, others } = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr-TR');

    const filtered = q
      ? malls.filter(
          (m) =>
            m.name.toLocaleLowerCase('tr-TR').includes(q) ||
            m.city.toLocaleLowerCase('tr-TR').includes(q) ||
            m.code.toLocaleLowerCase('tr-TR').includes(q),
        )
      : malls;

    // Arama yapılırken "son kullanılanlar" ayrımı kafa karıştırır;
    // kullanıcı zaten belirli bir şeyi arıyordur.
    if (q) {
      return { recent: [] as MallSummary[], others: [...filtered].sort(byName) };
    }

    const recentSet = new Set(recentMallIds);
    return {
      recent: recentMallIds
        .map((id) => filtered.find((m) => m.id === id))
        .filter((m): m is MallSummary => Boolean(m)),
      others: filtered.filter((m) => !recentSet.has(m.id)).sort(byName),
    };
  }, [malls, query, recentMallIds]);

  /*
    MENÜ AÇIK/KAPALI DURUMU KONTROL EDİLİR.

    react-bootstrap Dropdown yalnızca `Dropdown.Item` tıklamalarında
    kendiliğinden kapanır. Bu menüde seçenekler özel işaretlemeli
    (avatar + ad + m² + ünite sayısı) düz `<button>` öğeleridir, dolayısıyla
    otomatik kapanma DEVREYE GİRMEZ.

    Sonuç, kullanıcının gördüğü şuydu: AVM seçiliyor, sayfa arkada
    güncelleniyor ama menü açık kalıyor ve seçimin uygulanıp
    uygulanmadığı belirsiz görünüyor. Kullanıcı ikinci kez tıklıyor ve
    bu sefer menü kapanıyor — "iki kere tıklamak gerekiyor" hissi.

    Bu hatayı Playwright kapsam testi yakaladı: ikinci AVM seçimi,
    açık kalan menü yüzünden hiç yapılamıyordu.
  */
  const [open, setOpen] = useState(false);

  const handleSelect = (value: MallScopeValue) => {
    setMall(value);
    setQuery('');
    setOpen(false);
  };

  return (
    <Dropdown
      show={open}
      onToggle={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <Dropdown.Toggle
        as="button"
        type="button"
        className="mall-switcher__toggle"
        bsPrefix="mall-switcher__toggle"
        aria-label="AVM seç"
      >
        {activeMall ? (
          <>
            <span className="mall-switcher__logo">{activeMall.code}</span>
            <span className="mall-switcher__name">{activeMall.name}</span>
          </>
        ) : (
          <>
            <span className="mall-switcher__logo">
              <RiBuilding2Line size={14} aria-hidden="true" />
            </span>
            <span className="mall-switcher__name">Tüm AVM&apos;ler</span>
          </>
        )}
        <RiArrowDownSLine size={16} aria-hidden="true" className="ms-auto flex-shrink-0" />
      </Dropdown.Toggle>

      <Dropdown.Menu className="mall-switcher__menu">
        <div className="header-search mb-2 px-1" style={{ flex: '1 1 auto' }}>
          <input
            className="header-search__input"
            placeholder="AVM ara..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="AVM ara"
          />
          <RiSearchLine className="header-search__icon" size={16} aria-hidden="true" />
        </div>

        {canViewAll && !query && (
          <>
            <button
              type="button"
              className={`mall-switcher__option${mallId === ALL_MALLS ? ' is-active' : ''}`}
              onClick={() => handleSelect(ALL_MALLS)}
            >
              <span className="mall-switcher__logo">
                <RiBuilding2Line size={14} aria-hidden="true" />
              </span>
              <span className="flex-grow-1 text-start">
                <span className="d-block">Tüm AVM&apos;ler</span>
                <span className="fs-11 text-muted">Konsolide portföy görünümü</span>
              </span>
              {mallId === ALL_MALLS && <RiCheckLine size={16} aria-hidden="true" />}
            </button>
            <hr className="my-2" />
          </>
        )}

        {recent.length > 0 && (
          <>
            <div className="sidebar__section-label" style={{ padding: '0.25rem 0.6rem' }}>
              Son Kullanılan
            </div>
            {recent.map((m) => (
              <MallOption
                key={m.id}
                mall={m}
                active={m.id === mallId}
                onSelect={() => handleSelect(m.id)}
              />
            ))}
            <hr className="my-2" />
          </>
        )}

        {others.length > 0 ? (
          others.map((m) => (
            <MallOption
              key={m.id}
              mall={m}
              active={m.id === mallId}
              onSelect={() => handleSelect(m.id)}
            />
          ))
        ) : (
          <div className="text-muted fs-12 text-center py-3">Eşleşen AVM bulunamadı</div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function MallOption({
  mall,
  active,
  onSelect,
}: {
  mall: MallSummary;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`mall-switcher__option${active ? ' is-active' : ''}`}
      onClick={onSelect}
    >
      <span className="mall-switcher__logo">{mall.code}</span>
      <span className="flex-grow-1 text-start truncate">
        <span className="d-block truncate">{mall.name}</span>
        <span className="fs-11 text-muted">
          {mall.city} · {formatArea(mall.gla)} · {mall.unitCount} ünite
        </span>
      </span>
      {active && <RiCheckLine size={16} aria-hidden="true" />}
    </button>
  );
}

function byName(a: MallSummary, b: MallSummary): number {
  return trCollator.compare(a.name, b.name);
}
